import provincesData from '../data/provinces.json';
import cantonsData from '../data/cantons.json';
import districtsData from '../data/districts.json';

/** Provincia: código de 1 dígito ("1"–"7"). */
export interface Province {
  code: string;
  name: string;
}

/** Cantón: código de 3 dígitos = provincia (1) + cantón (2). */
export interface Canton {
  code: string;
  provinceCode: string;
  name: string;
}

/** Distrito: código de 5 dígitos = cantón (3) + distrito (2). */
export interface District {
  code: string;
  cantonCode: string;
  provinceCode: string;
  name: string;
}

export const PROVINCES: readonly Province[] = provincesData as readonly Province[];
export const CANTONS: readonly Canton[] = cantonsData as readonly Canton[];
export const DISTRICTS: readonly District[] = districtsData as readonly District[];

const PROVINCE_BY_CODE: ReadonlyMap<string, Province> = new Map(
  PROVINCES.map((p) => [p.code, p] as const),
);
const CANTON_BY_CODE: ReadonlyMap<string, Canton> = new Map(
  CANTONS.map((c) => [c.code, c] as const),
);
const DISTRICT_BY_CODE: ReadonlyMap<string, District> = new Map(
  DISTRICTS.map((d) => [d.code, d] as const),
);

function groupBy<T>(items: readonly T[], key: (item: T) => string): ReadonlyMap<string, readonly T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item);
    const bucket = map.get(k);
    if (bucket) {
      bucket.push(item);
    } else {
      map.set(k, [item]);
    }
  }
  return map;
}

const CANTONS_BY_PROVINCE = groupBy(CANTONS, (c) => c.provinceCode);
const DISTRICTS_BY_CANTON = groupBy(DISTRICTS, (d) => d.cantonCode);

const NO_CANTONS: readonly Canton[] = Object.freeze([]);
const NO_DISTRICTS: readonly District[] = Object.freeze([]);

/** Cantones de una provincia, en orden de código. Arreglo vacío si el código no existe. */
export function cantonsOfProvince(provinceCode: string): readonly Canton[] {
  return CANTONS_BY_PROVINCE.get(provinceCode) ?? NO_CANTONS;
}

/** Distritos de un cantón, en orden de código. Arreglo vacío si el código no existe. */
export function districtsOfCanton(cantonCode: string): readonly District[] {
  return DISTRICTS_BY_CANTON.get(cantonCode) ?? NO_DISTRICTS;
}

export function findProvince(code: string): Province | undefined {
  return PROVINCE_BY_CODE.get(code);
}

export function findCanton(code: string): Canton | undefined {
  return CANTON_BY_CODE.get(code);
}

export function findDistrict(code: string): District | undefined {
  return DISTRICT_BY_CODE.get(code);
}
